import React, { useEffect, useState } from 'react'
import { DebugJson } from '../../../../../components/DebugJson'
import { ConfigObject } from '../../../../../utils/deviceConfig'
import {
    buildTerminalConfigDiffFromState,
    defaultTerminalConfigFunctions,
    DefaultTerminalConfigSection,
    getInitialTerminalConfig,
    getStationsForSiblingLot
} from './convertTerminal'
import { DeviceDataType } from '../../../device-types'
import { launchToast, OverlaySpinner, ToastTypes } from '../../../../../components'
import { useFormSaveStore } from '../../../../../utils'
import _ from 'lodash'
import TerminalBasicConfigUI from './TerminalBasicConfigUI'
import { validateBasicConfig, ValidationErrors } from '../../utils/ValidationUtils'

export interface TerminalBasicConfigProps {
    deviceData: DeviceDataType
    selectedDevice: any
    editDiffConfig: (deviceId: string, config: any, resetFunc?: () => void) => void
    setDeviceForceUpdateAction: (deviceId: string) => void
    loadingSaveBasicConfig: boolean
    loadingForceUpdate: boolean
}

export interface TerminalBasicConfigState {
    // General
    test_mode: boolean
    lot_id: string
    // User Interface
    inactivity_timeout_ms: number
    inactivity_countdown_s: number
    inactivity_countdown_successful_page_s: number
    overpaid_countdown_s: number
    // Printer
    remove_printer_module: boolean
    // Cash
    remove_cash_module: boolean
    treasure_capacity: number
    currency_code: number
    // Cashless
    remove_cashless_module: boolean
    cashless_configure_enabled: boolean
    cashless_configure_job: string
    cashless_pu: number
    cashless_tid: number
    // Barcode
    remove_barcode_module: boolean
}

export const TerminalBasicConfig: React.FC<TerminalBasicConfigProps> = ({
    deviceData,
    selectedDevice,
    editDiffConfig,
    loadingSaveBasicConfig,
    loadingForceUpdate,
    setDeviceForceUpdateAction
}) => {
    const { base, diff } = deviceData?.config || {}

    const hasChanges = useFormSaveStore((state) => state.hasChanges)
    const setFormChanges = useFormSaveStore((state) => state.setFormChanges)

    const initialConfig = getInitialTerminalConfig(base, diff) || {}
    const avaiableLots = getStationsForSiblingLot(diff)
    const [terminalBasicState, setTerminalBasicState] = useState<TerminalBasicConfigState | null>(
        initialConfig as TerminalBasicConfigState
    )

    const [tempBuiltDiff, setTempBuiltDiff] = React.useState<ConfigObject | null>({})
    const [isValid, setIsValid] = useState<boolean>(false)
    const [validationErrors, setValidationErrors] = React.useState<ValidationErrors>({})

    React.useEffect(() => {
        const errors = validateBasicConfig(terminalBasicState)
        setValidationErrors(errors)
        setIsValid(Object.keys(errors).length === 0)
    }, [terminalBasicState])

    const handleUpdate = (key: keyof TerminalBasicConfigState, value: any) => {
        setTerminalBasicState((prev) => (prev ? { ...prev, [key]: value } : prev))
        setFormChanges(true)
    }

    const setDefaultToBaseForSection = (section: DefaultTerminalConfigSection) => {
        setFormChanges(true)
        const defaultSection = defaultTerminalConfigFunctions[section](base)
        setTerminalBasicState({
            ...terminalBasicState,
            ...(defaultSection as TerminalBasicConfigState)
        })
    }

    // Build the diff when the state changes
    React.useEffect(() => {
        setTempBuiltDiff(buildTerminalConfigDiffFromState(terminalBasicState, base, diff))
    }, [terminalBasicState])

    const triggerSave = () => {
        if (!isValid || !terminalBasicState) {
            launchToast(ToastTypes.Error, 'Please correct form errors before saving.')
            return
        }

        const diffConfig = buildTerminalConfigDiffFromState(terminalBasicState, base, diff)
        editDiffConfig(selectedDevice?.uuid, diffConfig, () => {
            setFormChanges(false)
        })
    }
    const handleForceUpdate = () => {
        if (window.confirm('Are you sure you want to force update the configuration for this device?')) {
            setDeviceForceUpdateAction(selectedDevice?.uuid)
        }

        // console.log('Force Update')
    }
    return terminalBasicState ? (
        <OverlaySpinner loadingTop={true} loading={loadingSaveBasicConfig || loadingForceUpdate}>
            <TerminalBasicConfigUI
                avaiableLots={avaiableLots}
                setDefaultToBaseForSection={setDefaultToBaseForSection}
                data={terminalBasicState}
                onSave={triggerSave}
                onForceUpdate={handleForceUpdate}
                onUpdate={handleUpdate}
                disabled={!hasChanges}
                forceUpdateDisabled={
                    !!deviceData.config_force_update ||
                    deviceData.config_current_timestamp === deviceData.config_target_timestamp
                }
                validationErrors={validationErrors}
                loadingSave={loadingSaveBasicConfig}
                loadingForceUpdate={loadingForceUpdate}
            />
            {/* <DebugJson src={tempBuiltDiff} showing={true} /> */}
        </OverlaySpinner>
    ) : (
        <></>
    )
}
